import React, { useState, useEffect, useRef } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { SearchIcon, ChevronUpIcon, SparklesIcon } from '@heroicons/react/solid'

import { useDebouncedEffect } from '@/utils/useDebouncedEffect'
import useQuery from '@/hooks/useQuery'
import Loading from '@/shared/Loading'
import Card from '@/shared/Card'
import Pagination from '@/shared/Pagination'
import { useGlobalState } from '@/state'

const CATEGORIES = [
  { key: '', label: 'All' },
  { key: 'feature_request', label: 'Feature Requests' },
  { key: 'bug_report', label: 'Bug Reports' }
]

// Multi-select pill list. Empty selection = no filter (show all).
const STATUSES = [
  { key: 'under_review', label: 'Under Review' },
  { key: 'planned', label: 'Planned' },
  { key: 'maybe', label: 'Maybe' },
  { key: 'complete', label: 'Complete' },
  { key: 'closed', label: 'Closed' }
]

const SORTS = [
  { key: 'most_voted', label: 'Most Voted' },
  { key: 'least_voted', label: 'Least Voted' },
  { key: 'newest', label: 'Newest' },
  { key: 'oldest', label: 'Oldest' },
  { key: 'recently_updated', label: 'Recently Updated' }
]

const STATUS_COLORS = {
  under_review: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  planned: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  maybe: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  complete: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  closed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
}

const CATEGORY_COLORS = {
  feature_request: 'bg-cccpurple/20 text-cccpurple dark:bg-purple-900/40 dark:text-purple-200',
  bug_report: 'bg-cccorange/20 text-cccorange dark:bg-orange-900/40 dark:text-orange-200'
}

const VoteButton = ({ feedback, onVote }) => {
  const [currentUser] = useGlobalState('currentUser')
  const { putpostRequest } = useQuery()
  const [voted, setVoted] = useState(feedback.voted)
  const [count, setCount] = useState(feedback.votesCount)

  const handleVote = (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (!currentUser?.id) {
      window.location.href = `/login?fwd=/feedback`
      return
    }

    setVoted(!voted)
    setCount(voted ? count - 1 : count + 1)
    putpostRequest(`/api/v3/feedbacks/${feedback.slug}/vote`, 'POST', {}, (err, jsonData) => {
      if (err) { setVoted(voted); setCount(count); return }
      setVoted(jsonData.voted)
      setCount(jsonData.votesCount)
    })
  }

  return (
    <button
      onClick={handleVote}
      className={`flex flex-col items-center justify-center px-3 py-2 rounded-lg border transition-colors min-w-[3.5rem] ${
        voted
          ? 'border-cccblue bg-cccblue/10 text-cccblue'
          : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-cccblue hover:text-cccblue'
      }`}
    >
      <ChevronUpIcon className='h-4 w-4' />
      <span className='text-sm font-semibold'>{count}</span>
    </button>
  )
}

const FeedbackRow = ({ feedback }) => {
  return (
    <Link to={`/feedback/${feedback.slug}`} className='flex items-center gap-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow'>
      <VoteButton feedback={feedback} />
      <div className='flex-1 min-w-0'>
        <div className='flex flex-wrap items-center gap-2 mb-1'>
          <h3 className='text-base font-semibold text-gray-900 dark:text-white truncate'>{feedback.title}</h3>
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[feedback.category]}`}>
            {feedback.category === 'feature_request' ? 'Feature' : 'Bug'}
          </span>
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[feedback.status]}`}>
            {feedback.status.replace('_', ' ')}
          </span>
        </div>
        <div className='flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400'>
          <span className='flex items-center gap-1'>
            <img className='h-4 w-4 rounded-sm' src={feedback.user.imageUrl} alt='' />
            {feedback.user.displayName}
          </span>
          <span>{feedback.createdAt}</span>
          <span>{feedback.commentsCount} comment{feedback.commentsCount !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </Link>
  )
}

const DEFAULT_STATUSES = ['under_review', 'planned', 'maybe']

const FeedbackIndex = () => {
  const [currentUser] = useGlobalState('currentUser')
  const [feedbacks, setFeedbacks] = useState([])
  const [loading, setLoading] = useState(true)
  const [meta, setMeta] = useState({})
  const location = useLocation()
  const history = useHistory()
  const [search, setSearch] = useState(() => new URLSearchParams(location.search).get('search') || '')
  const [category, setCategory] = useState(() => new URLSearchParams(location.search).get('category') || '')
  const [statusFilter, setStatusFilter] = useState(() => {
    const params = new URLSearchParams(location.search)
    // No status param at all → first-time visit, apply sensible default
    // (active stuff users care about). Empty string ("?status=") means
    // the user explicitly wanted no filter, so respect that.
    if (!params.has('status')) {
      return DEFAULT_STATUSES
    }
    return params.get('status').split(',').map((s) => s.trim()).filter(Boolean)
  })
  const [sort, setSort] = useState(() => new URLSearchParams(location.search).get('sort') || 'newest')
  const [page, setPage] = useState(1)
  const { getRequest } = useQuery()
  const scrollRef = useRef(null)

  const fetchData = (newPage) => {
    setLoading(true)
    const data = { page: newPage ? 1 : page, sort }
    if (search) data.search = search
    if (category) data.category = category
    if (statusFilter.length > 0) data.status = statusFilter.join(',')

    getRequest('/api/v3/feedbacks', data, (err, jsonData) => {
      setLoading(false)
      if (err) return
      setFeedbacks(jsonData.feedbacks)
      setMeta(jsonData.meta)
    })
  }

  const toggleStatus = (key) => {
    setStatusFilter((prev) => (
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]
    ))
  }

  useEffect(() => { fetchData() }, [page])
  useDebouncedEffect(() => { fetchData(true) }, 300, [search])
  useDebouncedEffect(() => { fetchData(true) }, 200, [category, statusFilter.join(','), sort])

  // First mount: if URL didn't carry status, bake the defaults into it
  // (replace, not push) so that bare /feedback and ?status=defaults are
  // never ambiguous. Without this, browser back from a filtered state
  // could land on "bare URL = defaults" and the URL-sync effect would
  // immediately push the defaults back into history, undoing the back.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (!params.has('status') && statusFilter.length > 0) {
      params.set('status', statusFilter.join(','))
      history.replace({ search: params.toString() })
    }
  }, [])

  // URL → state: react to browser back/forward and external nav.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const urlSearch = params.get('search') || ''
    const urlStatus = params.get('status')
    const urlStatusArr = urlStatus !== null
      ? urlStatus.split(',').map((s) => s.trim()).filter(Boolean)
      : DEFAULT_STATUSES
    const urlCategory = params.get('category') || ''
    const urlSort = params.get('sort') || 'newest'
    if (urlSearch !== search) setSearch(urlSearch)
    if (urlStatusArr.join(',') !== statusFilter.join(',')) setStatusFilter(urlStatusArr)
    if (urlCategory !== category) setCategory(urlCategory)
    if (urlSort !== sort) setSort(urlSort)
  }, [location.search])

  // state → URL: push so history captures every filter change.
  // Debounced so typing in the search box doesn't create a history entry per keystroke.
  useDebouncedEffect(() => {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    // Always emit status (even when empty) so that "user cleared all filters"
    // is distinguishable from "no param = defaults" on URL→state hydration.
    params.set('status', statusFilter.join(','))
    if (category) params.set('category', category)
    if (sort !== 'newest') params.set('sort', sort)
    const newSearch = params.toString()
    if (newSearch !== location.search.replace(/^\?/, '')) {
      history.push({ search: newSearch })
    }
  }, 200, [search, statusFilter.join(','), category, sort])

  return (
    <div className='max-w-3xl mx-auto'>
      <div className='flex justify-between items-center mb-6'>
        <p className='text-gray-500 dark:text-gray-400 text-sm'>
          Vote on features you want and report bugs you find.
        </p>
        {currentUser?.id && (
          <Link
            to='/feedback/new'
            className='inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-cccblue hover:bg-cccblue-alt transition-colors'
          >
            New Feedback
          </Link>
        )}
      </div>

      <a
        href='/changelog'
        className='block mb-6 rounded-2xl bg-gradient-to-br from-cccblue to-cccorange p-5 sm:p-6 text-white shadow-md hover:shadow-lg transition-shadow'
      >
        <div className='flex items-center justify-between gap-4'>
          <div>
            <p className='text-xs font-semibold uppercase tracking-wider opacity-90'>What's new</p>
            <h2 className='text-lg sm:text-xl font-bold mt-1'>See the latest product updates →</h2>
          </div>
          <SparklesIcon className='h-10 w-10 sm:h-12 sm:w-12 opacity-80 flex-shrink-0' />
        </div>
      </a>

      {/* Filters */}
      <div className='flex flex-col sm:flex-row gap-3 mb-4'>
        <div className='relative flex-1'>
          <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
            <SearchIcon className='h-4 w-4 text-gray-400' />
          </div>
          <input
            type='text'
            className='block w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-2.5 pl-9 pr-3 text-sm'
            placeholder='Search feedback...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className='rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 dark:text-gray-100 py-2.5 px-3 text-sm'
        >
          {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      {/* Status pills (OR-multi-select). Empty = show all. */}
      <div className='flex flex-wrap gap-2 mb-4 items-center'>
        {STATUSES.map((s) => {
          const active = statusFilter.includes(s.key)
          // ring-current makes each active pill ring match its own text color,
          // so even gray-on-gray ("maybe") stays visibly distinct from inactive.
          const activeStyle = `${STATUS_COLORS[s.key]} ring-2 ring-current`
          const inactiveStyle = 'bg-transparent text-gray-500 dark:text-gray-400 ring-1 ring-gray-200 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
          return (
            <button
              key={s.key}
              type='button'
              onClick={() => toggleStatus(s.key)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize transition-colors ${active ? activeStyle : inactiveStyle}`}
              aria-pressed={active}
            >
              {s.label}
            </button>
          )
        })}
        {statusFilter.length > 0 && (
          <button
            type='button'
            onClick={() => setStatusFilter([])}
            className='text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 underline ml-1'
          >
            clear
          </button>
        )}
      </div>

      {/* Category tabs */}
      <div className='flex gap-2 mb-4'>
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setCategory(c.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              category === c.key
                ? 'bg-cccblue text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div ref={scrollRef} />

      {loading && <Loading />}

      {!loading && feedbacks.length === 0 && (
        <p className='text-center text-sm text-gray-500 dark:text-gray-400 py-12'>
          No feedback found. {currentUser?.id ? 'Be the first to submit one!' : ''}
        </p>
      )}

      {!loading && feedbacks.length > 0 && (
        <>
          <div className='flex flex-col gap-3'>
            {feedbacks.map((fb) => <FeedbackRow key={fb.id} feedback={fb} />)}
          </div>
          <Pagination meta={meta} callback={setPage} scrollRef={scrollRef} />
        </>
      )}
    </div>
  )
}

export default FeedbackIndex
